import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import InlineError from "../components/inline-error.js";
import { FillContainerLoader } from "../components/loader.js";
import trpc from "../utils/trpc.js";
import useCurrentModuleItemId from "../hooks/use-current-module-item-id.js";
import { sanitizeCanvasHtml } from "../utils/canvas.js";
import { Button } from "../components/button/button.js";
import { CheckCircleIcon, InformationCircleIcon, XCircleIcon } from "@heroicons/react/24/outline";
import { QuizQuestionCard } from "../components/quiz-question-card.js";

type SubmitResult = {
  pointsEarned: number;
  pointsPossible: number;
  minScore?: number;
  passed: boolean;
  completed: boolean;
  assessmentId?: string;
  alreadySubmitted?: boolean;
};

export function Component() {
  const { quizId } = useParams<{ quizId: string }>();
  const currentModuleItemId = useCurrentModuleItemId();

  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [result, setResult] = useState<SubmitResult | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    data: quizData,
    isLoading: isQuizLoading,
    error,
  } = trpc.course.getQuiz.useQuery(
    { quizId: quizId ?? "", moduleItemId: currentModuleItemId ? String(currentModuleItemId) : undefined },
    { staleTime: Infinity, enabled: Boolean(quizId) },
  );

  const submitMutation = trpc.course.submitQuiz.useMutation();
  const utils = trpc.useUtils();

  // Revisiting an already-submitted assessment-linked quiz: show the saved answers read-only
  useEffect(() => {
    const existingResult = quizData?.existingResult;
    if (!existingResult || result) return;
    setAnswers(existingResult.answers);
    setResult({
      pointsEarned: existingResult.pointsEarned,
      pointsPossible: existingResult.pointsPossible,
      minScore: existingResult.minScore,
      passed: existingResult.passed,
      completed: true,
      assessmentId: quizId,
      alreadySubmitted: false,
    });
  }, [quizData, result, quizId]);

  // Collect all question entry ids that need an answer
  const questionEntryIds = useMemo(() => {
    if (!quizData) return [];
    return quizData.items
      .map(item => {
        const entry = item.entry;
        return entry ? String(entry["id"]) : null;
      })
      .filter((id): id is string => id !== null);
  }, [quizData]);

  const allAnswered = questionEntryIds.length > 0 && questionEntryIds.every(id => answers[id] !== undefined);

  const handleSelect = useCallback((questionEntryId: string, choiceId: string) => {
    setAnswers(prev => ({ ...prev, [questionEntryId]: choiceId }));
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!quizId || !currentModuleItemId || !allAnswered) return;
    setIsSubmitting(true);
    try {
      const res = await submitMutation.mutateAsync({
        moduleItemId: String(currentModuleItemId),
        quizId,
        answers,
      });
      setResult(res);
      await utils.progression.get.invalidate();
      // This quiz is tied to an Assessment, so the journey may have changed based on the score
      if (res.assessmentId) {
        await utils.course.getModules.invalidate();
      }
    } catch (e) {
      console.error(e);
    } finally {
      setIsSubmitting(false);
    }
  }, [quizId, currentModuleItemId, allAnswered, answers, submitMutation, utils]);

  const handleRetry = useCallback(() => {
    setAnswers({});
    setResult(null);
  }, []);

  if (isQuizLoading) return <FillContainerLoader delayMs={0} />;
  if (error) return <InlineError message="We couldn't load this quiz. Please try refreshing." />;
  if (!quizData) return <div className="text-sm">No quiz available.</div>;

  const { quiz, items } = quizData;

  return (
    <>
      {quiz.instructions && (
        <div
          className="_isolated-root mb-6 leading-relaxed text-gray-80"
          dangerouslySetInnerHTML={{ __html: sanitizeCanvasHtml(quiz.instructions) }}
        />
      )}

      {/* Questions */}
      <div className="space-y-8">
        {items.map((item, index) => {
          const entry = item.entry;
          if (!entry) return null;

          const entryId = String(entry["id"]);
          const itemBody = entry["item_body"];
          const interactionData = entry["interaction_data"] as
            { choices?: Array<{ id: string; item_body: string }> } | undefined;
          const choices = interactionData?.choices ?? [];

          return (
            <QuizQuestionCard
              key={entryId}
              index={index}
              entryId={entryId}
              itemBody={itemBody}
              choices={choices}
              selectedChoiceId={answers[entryId]}
              onSelect={choiceId => handleSelect(entryId, choiceId)}
              disabled={Boolean(result)}
            />
          );
        })}
      </div>

      {/* Result panel */}
      {result &&
        (() => {
          if (result.alreadySubmitted) {
            return (
              <div className="mt-8 rounded-xl border border-lac-leman bg-lac-leman/10 p-5">
                <div className="flex items-center gap-3">
                  <InformationCircleIcon className="h-6 w-6 shrink-0 text-lac-leman" />
                  <p className="font-semibold">This assessment has already been submitted</p>
                </div>
              </div>
            );
          }

          // Assessment-tied quizzes hide pass/fail, points and try again button, unless a min score threshold makes that info meaningful
          const hideDetails = Boolean(result.assessmentId) && result.minScore == null;
          return (
            <div
              className={`mt-8 rounded-xl border p-5 ${
                hideDetails || result.passed ? "border-lavaux bg-lavaux/10" : "border-error bg-error/10"
              }`}>
              <div className="flex items-center gap-3">
                {hideDetails || result.passed ? (
                  <CheckCircleIcon className="text-green-600 h-6 w-6 shrink-0" />
                ) : (
                  <XCircleIcon className="h-6 w-6 shrink-0 text-error" />
                )}
                <div>
                  {hideDetails ? (
                    <p className="font-semibold">Submitted</p>
                  ) : (
                    <>
                      <p className="font-semibold">
                        {result.pointsEarned} / {result.pointsPossible} points
                      </p>
                      {result.minScore != null && (
                        <p className="text-gray-600 mt-0.5 text-sm">
                          {result.passed
                            ? `Passed — required ${result.minScore} points`
                            : `Not passed — ${result.minScore} points required to pass`}
                        </p>
                      )}
                      {result.minScore == null && result.passed && (
                        <p className="text-gray-600 mt-0.5 text-sm">Submitted</p>
                      )}
                    </>
                  )}
                </div>
              </div>
              {!hideDetails && !result.alreadySubmitted && (
                <div className="mt-4">
                  <Button onClick={handleRetry} className="px-4 py-1.5 text-sm">
                    Try again
                  </Button>
                </div>
              )}
            </div>
          );
        })()}

      {/* Submit button */}
      {!result && (
        <div className="mt-8">
          <Button
            filled="lac-leman"
            disabled={!allAnswered || isSubmitting}
            loading={isSubmitting}
            onClick={handleSubmit}
            className="px-6 py-2 text-sm">
            Submit
          </Button>
        </div>
      )}
    </>
  );
}
